/**
 * Visualizer link construction.
 *
 * Every outbound link to a vendor's visualizer is built here so that attribution
 * has exactly one choke point. A link that leaves the backoffice — pasted into a
 * social caption, an email, an ad — carries the params the visualizer reads back
 * on landing (see ai-visualizer `common/utils/attribution.ts`), which is what
 * lets a lead be traced to the content that produced it.
 *
 * Params are deliberately the standard `utm_*` names plus Ardealy's `ard_*` ids,
 * so links keep working when a vendor pastes them into a third-party scheduler
 * or an ad platform re-tags them.
 */

import { getPreviewTag } from '../services/preview-tag.service';

export interface VisualizerLinkAttribution {
    /** Channel — 'instagram', 'email', 'linkedin'… Maps to `utm_source`. */
    source?: string;
    /** Grouping — 'social', 'email', 'cpc'. Maps to `utm_medium`. */
    medium?: string;
    /** Campaign identifier. Maps to `utm_campaign`. */
    campaign?: string;
    /** Creative variant, for telling A/B versions apart. Maps to `utm_content`. */
    content?: string;
    /** CsAsset.uuid behind the creative. Maps to `ard_asset`. */
    assetUuid?: string;
    /** CsSocialPost.uuid the link was published in. Maps to `ard_post`. */
    postUuid?: string;
}

export interface VisualizerLinkOptions {
    /** Vendor alias — the visualizer's tenant segment. */
    alias: string;
    /** Optional product SKU to open the visualizer on. */
    sku?: string;
    /** Attribution to stamp onto the link. Omitted params are simply absent. */
    attribution?: VisualizerLinkAttribution;
}

const ATTRIBUTION_PARAMS: Array<[keyof VisualizerLinkAttribution, string]> = [
    ['source', 'utm_source'],
    ['medium', 'utm_medium'],
    ['campaign', 'utm_campaign'],
    ['content', 'utm_content'],
    ['assetUuid', 'ard_asset'],
    ['postUuid', 'ard_post'],
];

/** Base visualizer origin, without a trailing slash. */
function visualizerOrigin(): string {
    return (process.env.REACT_APP_VISUALIZER_URL || '').replace(/\/+$/, '');
}

/**
 * Build a link to a vendor's visualizer.
 *
 * Returns an empty string when `alias` is missing, so callers rendering a copy
 * or share affordance can disable it rather than produce a broken URL.
 */
export function buildVisualizerUrl({ alias, sku, attribution }: VisualizerLinkOptions): string {
    if (!alias) return '';

    const params = new URLSearchParams();
    if (sku) params.set('sku', sku);

    if (attribution) {
        for (const [field, param] of ATTRIBUTION_PARAMS) {
            const value = attribution[field];
            if (value) params.set(param, value);
        }
    }

    // A preview from the backoffice is our own visit (or the vendor's own team's):
    // the tag has the API keep it apart from real visitors. Only on previews —
    // a link meant for customers (a post, an email, an ad) never carries it.
    if (attribution?.medium === PREVIEW_LINK_ATTRIBUTION.medium) {
        const tag = getPreviewTag();
        if (tag) params.set('ardealy_team', tag);
    }

    const query = params.toString();
    return `${visualizerOrigin()}/visualizer/${alias}${query ? `?${query}` : ''}`;
}

/**
 * Attribution for the "open in visualizer" affordances inside the backoffice.
 *
 * These are vendors and admins checking their own work, not real visitors —
 * untagged they are indistinguishable from customer traffic and quietly inflate
 * a vendor's own numbers. Tagging them makes them filterable.
 */
export const PREVIEW_LINK_ATTRIBUTION: VisualizerLinkAttribution = {
    source: 'backoffice',
    medium: 'preview',
};
